import { useState, useContext } from 'react';
import { ApiContext } from 'utils/networking/Api.context';
import { groupByKey } from 'utils/TextUtils';
import { notification } from 'antd';
import { AppContext } from 'App.context';
import { show2FAModal } from 'Components/common/TwoFactorModal/TwoFactorModal';
import dynamicFormsStyles from '../../Forms/DynamicForms/DynamicForms.styles';

export default function usePmsConfigHook(setPmsCheckin, rowData, hotelPms) {
  const [state, setState] = useState({
    searchByCodeResult: null,
    searchByCodeResponse: null,
    searchByEntranceDateResult: null,
    // searchByHolderNameResult: null,
    searchByIdResult: null,
    isLoading: false,
    data: null,
    orderedConfig: {},
    isTesting: false,
    testResult: null,
    isTestingBooking: false,
    testBookingResult: null,
    showResults: false,
    parameters: {
      bookingIdentifier: false,
      bookingCode: false,
      entranceDate: false,
      holderName: false,
    },
  });

  const { API } = useContext(ApiContext);
  const {
    appActions: { translate },
  } = useContext(AppContext);

  const actions = {
    loadPmsConfig: async (pms, hotelId) => {
      if (pms.connectionType !== 'form') return;
      updateState({ isLoading: true });

      API.pmsConfigRequest({ pmsId: pms.id, hotelId })
        .then(res => {
          for (const field of res) {
            if (field.type === 'split') {
              field.splitValues = field.fields.map(() => '');

              if (field.value) {
                field.value.split(field.separator).forEach((subFieldValue, index) => {
                  if (subFieldValue) {
                    //DocumentTypes in different fields (Protel, Ohip and Sihot360)
                    if ([21, 30, 54].includes(pms.id) && field.key != 'pms_key') {
                      const splitIndex = subFieldValue.indexOf('=');
                      field.splitValues[index] =
                        splitIndex !== -1 ? subFieldValue.slice(splitIndex + 1) : subFieldValue;
                    } else {
                      field.splitValues[index] = subFieldValue;
                    }
                  }
                });
              }
            }
          }

          const orderedConfig = groupByKey(res, 'category');

          // In case selected PMS is not the one the hotel has saved, set values to empty
          if (hotelPms[hotelId] !== pms?.name) {
            Object.entries(orderedConfig).forEach(([key, fields]) => {
              if (key !== 'configFilesCheckboxesAllowed') {
                fields.forEach(field => {
                  field.value = '';
                  if (field.type === 'split' && Array.isArray(field.splitValues)) {
                    field.splitValues.fill('');
                  }
                });
              }
            });
          }

          updateState({ data: res, isLoading: false, orderedConfig });
        })
        .catch(error => {
          updateState({ isLoading: false });
          console.error('Error loading PMS config:', error);
        });
    },

    onValueChanged: (key, text, splitValues, table) => {
      const dataCopy = state.data.map(field => {
        if (field.key === key && (!table || table === field.table)) {
          field.value = text;
          if (splitValues) {
            field.splitValues = splitValues;
          }
        }
        return field;
      });

      updateState({ data: dataCopy });
    },

    getFieldValue: (key, table) => {
      const index = state.data.findIndex(x => x.key === key && (table ? x.table === table : true));
      return state.data[index]?.value;
    },

    onSave: async (pms, hotelId, currentPms) => {
      updateState({ isLoading: true });

      const twoFAResult = await show2FAModal();

      if (twoFAResult === true) {
        API.updatePmsConfigRequest({ hotelId, fields: state.data ?? [], pms })
          .then(_ => {
            API.pmsConfigHotelRequest({ hotelId, pmsName: pms.name, pmsId: pms.id })
              .then(_ => {
                setPmsCheckin(hotelId, pms.name);
              })
              .catch(err => console.error(err));
          })
          .catch(_ => {
            updateState({ isLoading: false });
          })
          .finally(() => {
            actions.loadPmsConfig(pms, hotelId);
            updateState({ isLoading: false });
          });
      } else {
        updateState({ isLoading: false });
      }
    },

    doAutomaticTest: async hotelId => {
      let connectionResponse;
      try {
        connectionResponse = await API.connectionValidationRequest({
          hotelId,
        });
      } catch (error) {
        console.error('Error during connection validation:', error);
      }

      connectionResponse?.success
        ? notification.success({
            message: translate('pmsScreen.connectSuccess'),
          })
        : notification.error({ message: translate('pmsScreen.connectError') });
    },
    doBookingTest: async (bookingData, hotelId) => {
      // Inicializar el estado antes de la prueba
      updateState({ isTestingBooking: true, showResults: false });

      try {
        // Realizar la solicitud de validación de búsqueda
        const searchesResponse = await API.searchesValidationRequest({ hotelId, bookingData });

        // Desestructurar los resultados de búsqueda de la respuesta
        const {
          searchByCodeResult,
          searchByEntranceDateResult,
          // searchByHolderNameResult,
          searchByIdResult,
        } = searchesResponse;

        // Verificar si los parámetros están presentes en los datos de reserva
        const parameters = {
          bookingIdentifier: Boolean(bookingData.bookingIdentifier),
          bookingCode: Boolean(bookingData.bookingCode),
          entranceDate: Boolean(bookingData.entranceDate),
          // holderName: Boolean(bookingData.holderName),
        };

        // Se contempla si ha fallado la busqueda inicial en el API necesaria para el resto de busquedas
        // if (searchesResponse.hasOwnProperty('success') && !searchesResponse?.success) {
        //   updateState({
        //     searchByCodeResult: true,
        //     searchByEntranceDateResult: true,
        //     searchByIdResult: true,
        //     isTestingBooking: false,
        //     showResults: true,
        //     parameters,
        //   });
        //   return;
        // }

        // Manejar cada resultado de búsqueda, asegurando que los errores se capturen adecuadamente

        let codeResult = searchByCodeResult;
        if (searchByCodeResult.error && typeof searchByCodeResult.error === 'string') {
          try {
            codeResult = { ...searchByCodeResult, response: JSON.parse(searchByCodeResult.error) };
          } catch (e) {
            codeResult = { response: { errorCode: 500 } };
          }
        }

        let entranceDateResult = searchByEntranceDateResult;
        if (
          searchByEntranceDateResult.error &&
          typeof searchByEntranceDateResult.error === 'string'
        ) {
          try {
            entranceDateResult = {
              ...searchByEntranceDateResult,
              response: JSON.parse(searchByEntranceDateResult.error),
            };
          } catch (e) {
            entranceDateResult = { response: { errorCode: 500 } };
          }
        }

        // const holderNameResult = parameters.holderName
        //   ? searchByHolderNameResult
        //   : { success: false, error: 'No se proporcionó holderName o no se pudo validar.' };

        const idResult = searchByIdResult;
        if (searchByIdResult.error && typeof searchByIdResult.error === 'string') {
          try {
            idResult = { ...searchByIdResult, response: JSON.parse(searchByIdResult.error) };
          } catch (e) {
            idResult = { response: { errorCode: 500 } };
          }
        }

        // Actualizar el estado con los resultados de las búsquedas y mostrar los resultados
        updateState({
          searchByCodeResult: codeResult,
          searchByEntranceDateResult: entranceDateResult,
          // searchByHolderNameResult: holderNameResult,
          searchByIdResult: idResult,
          isTestingBooking: false,
          showResults: true,
          parameters,
        });
      } catch (error) {
        // Manejo de errores en la llamada a la API
        console.error('Error during booking test:', error);
        updateState({
          isTestingBooking: false,
          showResults: true,
          searchByCodeResult: {
            success: false,
            error: 'Error de red o del servidor al buscar por código.',
          },
          searchByEntranceDateResult: {
            success: false,
            error: 'Error de red o del servidor al buscar por fecha de entrada.',
          },
          // searchByHolderNameResult: {
          //   success: false,
          //   error: 'Error de red o del servidor al buscar por nombre del titular.',
          // },
          searchByIdResult: {
            success: false,
            error: 'Error de red o del servidor al buscar por ID.',
          },
        });
      }
    },

    doSearchByCodeTest: async (bookingData, hotelId) => {
      updateState({ isTestingBooking: true, showResults: false });

      try {
        const searchByCodeResponse = await API.searchByCodeValidationRequest({
          hotelId,
          bookingData,
        });
        let response = {};

        if (searchByCodeResponse.response && typeof searchByCodeResponse.response === 'object') {
          response = searchByCodeResponse.response;
        } else if (searchByCodeResponse.error && typeof searchByCodeResponse.error === 'string') {
          try {
            response = JSON.parse(searchByCodeResponse.error);
          } catch (e) {
            response = { errorCode: 500 };
          }
        }

        updateState({
          searchByCodeResult: searchByCodeResponse.success,
          searchByCodeResponse: response,
          isTestingBooking: false,
          showResults: true,
        });
      } catch (error) {
        console.error('Error during booking test:', error);
        updateState({
          isTestingBooking: false,
          showResults: true,
          searchByCodeResult: {
            success: false,
            error: 'Error de red o del servidor al buscar por código.',
          },
        });
      }
    },

    mergeCustomFieldsAndCamelCase(pmsData) {
      if (!pmsData) return null;

      let newGuests = [];

      pmsData.map(guest => {
        guest = { ...guest.CustomFields, ...guest, ...guest.LegalFields };

        let newGuest = {};
        for (const key in guest) {
          let keyLower = toCamelCase(key);
          if (key === 'document_support_number') {
            newGuest[keyLower] = guest[key];
            keyLower = key;
          }
          if (key != 'CustomFields' && key != 'legalFields') newGuest[keyLower] = guest[key];
        }
        const newGuestFormated = parseGuestToDDBB(newGuest);
        newGuests.push(newGuestFormated);
      });

      return newGuests;
    },

    excludeNonPmsVariables(fields, supportedFields, selectedGuest) {
      if (!supportedFields || !selectedGuest) return fields;
      if (!fields) return [];
      // supportedFields.push('pmsIdentifier');
      // if (supportedFields.some(field => field === 'documentSupportNumber')) {
      //   supportedFields.push('document_support_number');
      // }

      // for (let i = fields?.length - 1; i >= 0; i--) {
      //   if (fields[i] === 'pmsIdentifier') {
      //     fields.splice(i, 1);
      //   }
      // if (!supportedFields.includes(fields[i])) {
      //   fields.splice(i, 1);
      // }
      // }
      // fields.unshift('pmsIdentifier');

      const docCamel = 'documentSupportNumber';
      const docSnake = 'document_support_number';

      const haveCamel = fields.includes(docCamel);
      const haveSnake = fields.includes(docSnake);

      if (haveCamel && haveSnake) {
        const hasCamelValue = !!selectedGuest[docCamel];
        const hasSnakeValue = !!selectedGuest[docSnake];

        let toRemove;
        if (hasCamelValue) {
          toRemove = docSnake;
        } else if (hasSnakeValue) {
          toRemove = docCamel;
        } else {
          toRemove = docSnake;
        }

        fields = fields.filter(field => field !== toRemove);
      }

      return fields;
    },

    isNonPmsVariable({ key, supportedFields, pmsModuleName, pmsUdfState, snakeToCamel }) {
      if (!key || !supportedFields || !pmsModuleName || !pmsUdfState || !snakeToCamel) return null;

      supportedFields.push('pmsIdentifier');
      if (!supportedFields.includes(key)) {
        switch (
          getFieldStatus({
            key,
            supportedFields,
            pmsModuleName,
            pmsUdfState,
            snakeToCamel,
          })
        ) {
          case 'requiresMapping':
            return (
              <div
                style={{
                  ...dynamicFormsStyles.fieldStatus,
                  color: '#FF9800',
                  backgroundColor: '#FFF3E0',
                  display: 'inline-flex',
                  alignItems: 'center',
                }}
              >
                <div style={{ ...dynamicFormsStyles.iconStyle, backgroundColor: '#FF9800' }} />
                {translate('guestTable.requiresMapping')}
              </div>
            );
          case 'unavailable':
            return (
              <div
                style={{
                  ...dynamicFormsStyles.fieldStatus,
                  color: '#D32F2F',
                  backgroundColor: '#FDE8E8',
                  display: 'inline-flex',
                  alignItems: 'center',
                }}
              >
                <div style={{ ...dynamicFormsStyles.iconStyle, backgroundColor: '#D32F2F' }} />
                {translate('guestTable.unavailableInPMS')}
              </div>
            );
          default:
            return null;
        }
      }
      return null;
    },

    parseToLabel(str) {
      if (!str) return '';

      if (str.includes('_')) {
        return str
          .split('_')
          .map((word, i) =>
            i === 0
              ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
              : word.toLowerCase()
          )
          .join(' ');
      }

      const withSpaces = str.replace(/([a-z])([A-Z])/g, '$1 $2');

      return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1).toLowerCase();
    },

    resetBookingsResult() {
      setState(prev => ({
        ...prev,
        searchByCodeResult: null,
        searchByCodeResponse: null,
        searchByEntranceDateResult: null,
        searchByIdResult: null,
        showResults: false,
        parameters: {
          bookingIdentifier: false,
          bookingCode: false,
          entranceDate: false,
          holderName: false,
        },
      }));
    },
  };

  // Función para actualizar el estado
  function updateState(object) {
    setState(previousState => ({ ...previousState, ...object }));
  }

  function toCamelCase(str) {
    return str
      .replace(/[-_\s]+(.)/g, (_, char) => char.toUpperCase())
      .replace(/^([A-Z])/, (match, char) => char.toLowerCase());
  }

  function parseGuestToDDBB(data) {
    const mapping = {
      gender: 'sex',
      language: 'lang',
    };

    let transformed = {};

    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        const newKey = mapping[key] || key;
        transformed[newKey] = data[key];
      }
    }
    return transformed;
  }
  function getFieldStatus({ key, supportedFields, pmsModuleName, pmsUdfState, snakeToCamel }) {
    const fieldNameCamel = snakeToCamel(key).trim();
    const isAvailable =
      supportedFields.some(f => f.trim() === fieldNameCamel) ||
      pmsUdfState?.hotelUdfs?.some(udf => snakeToCamel(udf.validate) === fieldNameCamel);

    if (key == 'nationality' && pmsModuleName == 'Ohip') return 'requiresMapping';

    if (isAvailable) return 'available';
    if (['Cloudbeds', 'Cloudbeds_new', 'Ohip', 'Opera'].includes(pmsModuleName))
      return 'requiresMapping';
    return 'unavailable';
  }

  return { state, actions };
}
